import { Body, TextLink } from '@assembly-js/design-system';
import React from 'react';

import {
  AppPresets,
  EXTENSION_SETUP_LINK,
  isEmbedAnyAppPreset,
} from 'src/constants';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';
import { RouteContext } from 'src/context';
import { useContentfulApps } from 'src/hooks/useContentfulApps';
import { BaseDescription } from 'src/legacy/components/CustomizationForm/BaseDescription';

import { ModuleTypes } from './appSetupTypes';
import { useEditingApp } from './useEditingApp';

const SetupAppPageDescription = () => {
  const { query } = React.useContext(RouteContext);
  const { getAppInfo, getCoreApp } = useContentfulApps();
  const { moduleId, moduleType, preset = '' } = query;
  const currentEditingModule = useEditingApp();
  // The Partner App setup flow is surfaced as "Embed any app" in the redesigned
  // browse experience.
  const isEmbedAnyAppFlow = isEmbedAnyAppPreset(preset);
  // get app infomation using it's title
  const customModuleCard = getAppInfo(preset as string);
  const [descriptionData, setDescriptionData] = React.useState<any>(null);

  /**
   * Method to get the title of the form
   * we use `preset` query param to check the selected app
   * @returns
   */
  const getAddFormTitle = () => {
    if (isEmbedAnyAppPreset(preset)) return 'a Partner App';

    if (preset === AppPresets.CustomApp) return 'a Custom App';

    return getAppInfo(preset)?.name || '';
  };

  /**
   * Method to get the learn more link label and href value
   * we use 'preset' query paran to check the selected app
   * @returns
   */
  const getLearnMoreLinkData = () => {
    if (isEmbedAnyAppPreset(preset))
      return {
        label: isEmbedAnyAppFlow
          ? 'Learn how to set up Embed any app'
          : 'Learn how to set up a Partner App',
        link: EXTENSION_SETUP_LINK,
      };

    if (preset === AppPresets.CustomApp)
      return {
        label: 'Learn how to set up a Custom App',
        link: `https://www.${primaryDomainHostname.prod}/guide/custom-apps`,
      };

    const app = getAppInfo(preset);

    return {
      label: `Learn how to set up ${app?.name || ''}`,
      link: app?.learnMoreLink || EXTENSION_SETUP_LINK,
    };
  };

  const getCoreAppLearnMoreLink = (coreModuleId: string) => {
    if (coreModuleId !== '') {
      const coreApp = getCoreApp(coreModuleId);
      if (coreApp) {
        return `https://www.${primaryDomainHostname.prod}/guide/${coreApp.guideSlug}`;
      }
      return '';
    }

    return '';
  };

  React.useEffect(() => {
    // add extension flow
    if (moduleId === 'new') {
      setDescriptionData({
        title: isEmbedAnyAppFlow ? 'Embed any app' : `Add ${getAddFormTitle()}`,
        description: (
          <TextLink href={getLearnMoreLinkData().link} target="_blank">
            <Body size="sm">{getLearnMoreLinkData().label}</Body>
          </TextLink>
        ),
      });
      return;
    }

    // edit module page
    if (currentEditingModule) {
      if (moduleType === ModuleTypes.Core) {
        // Generated (App Builder) apps are opened here with moduleType 'module'
        // but have no Contentful core-app entry, so the guide link resolves
        // empty — omit it rather than render an href="" that reloads the page.
        const coreLearnMoreLink = getCoreAppLearnMoreLink(
          currentEditingModule.id,
        );
        // when core module is disabled, it should say Enable {moduleName}
        setDescriptionData({
          title: `${currentEditingModule.disabled ? 'Enable' : 'Edit'} ${
            currentEditingModule.label
          }`,
          description: coreLearnMoreLink ? (
            <TextLink href={coreLearnMoreLink} target="_blank">
              <Body size="sm">
                Learn how to set up {currentEditingModule.label} App
              </Body>
            </TextLink>
          ) : null,
        });
        return;
      }

      setDescriptionData({
        title: `Edit ${currentEditingModule.label}`,
        description: (
          <TextLink target="_blank" href={EXTENSION_SETUP_LINK}>
            <Body size="sm">Learn how to set up an app</Body>
          </TextLink>
        ),
      });
    }
  }, [
    moduleId,
    currentEditingModule,
    customModuleCard,
    moduleType,
    isEmbedAnyAppFlow,
  ]);

  return (
    <BaseDescription
      titleFontType="24Medium"
      title={descriptionData?.title}
      descriptions={[descriptionData?.description]}
    />
  );
};

export default SetupAppPageDescription;
